import { Add } from "assets/icons/Common";
import { Row } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { useCallback, useContext, useEffect, useState, FC } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { WidgetSettingFC } from "types/Setting";
import { getFullDateString } from "util/getDateString";

import { CurrentScheduleSettingItem } from "./CurrentScheduleSettingItem";
import { EditActivityModal } from "./EditActivityModal";
import { ScheduleItemData } from "./interfaces";
import { SavedSchedule } from "./OrganiseScheduleSetting";
import { FooterButton, Header, OuterContainer, ScheduleContainer } from "./scheduleSetting.styles";

interface Props {
  assignedActivities: ScheduleItemData[];
  updateActivityOrder: (data: ScheduleItemData[]) => void;
  setAssignedActivities: (data: ScheduleItemData[]) => void;
  selectedDate: Date;
  activeSchedule: SavedSchedule | undefined;
}

export const AssignedActivities: FC<Props> = ({
  assignedActivities,
  updateActivityOrder,
  setAssignedActivities,
  selectedDate,
  activeSchedule,
}) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const {
    data: { assignedSchedules },
    setData: updateGlobalData,
  } = useContext(DashboardContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState<number | undefined>();

  const deleteScheduleItem = (index: number) => {
    let newData = [...assignedActivities];
    newData.splice(index, 1);
    setAssignedActivities(newData);
  };

  const handleUpdateOrder = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newData = [...assignedActivities];
      newData.splice(dragIndex, 1);
      newData.splice(hoverIndex, 0, assignedActivities[dragIndex]);

      setAssignedActivities(newData);
      updateActivityOrder(newData);
    },
    [assignedActivities, setAssignedActivities],
  );

  const selectScheduleItem = (index: number) => {
    setSelectedActivityIndex(index);
    setShowModal(true);
  };

  const handleActivityEdit = (newActivityData: ScheduleItemData) => {
    if (selectedActivityIndex !== undefined) {
      const fullDateString = getFullDateString(selectedDate);
      const newAssignedSchedule = {
        ...assignedSchedules[fullDateString],
        schedule: [...assignedSchedules[fullDateString].schedule],
      };
      newAssignedSchedule.schedule[selectedActivityIndex] = newActivityData;
      const newAssignedSchedules = {
        ...assignedSchedules,
        [fullDateString]: newAssignedSchedule,
      };
      updateGlobalData({ assignedSchedules: newAssignedSchedules });
    }
  };

  return (
    <OuterContainer>
      <Header>
        {selectedDate
          ? `${days[selectedDate.getDay()]}, ${selectedDate.getDate()} ${months[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
          : "Select your date"}
      </Header>
      <DndProvider backend={HTML5Backend} context={window}>
        <ScheduleContainer>
          {assignedActivities.length < 1 && (
            <p>Please add some activities from the Activity Bank below to the schedule.</p>
          )}
          {assignedActivities &&
            assignedActivities.map((scheduleItem, index) => {
              if (scheduleItem) {
                const handleDelete = () => deleteScheduleItem(index);
                const handleClick = () => selectScheduleItem(index);

                return (
                  <CurrentScheduleSettingItem
                    key={scheduleItem.id}
                    item={scheduleItem}
                    onUpdateOrder={handleUpdateOrder}
                    onDelete={handleDelete}
                    onClick={
                      activeSchedule
                        ? handleClick
                        : () => window.alert("Please save the schedule first to edit the activity.")
                    }
                    index={index}
                    dataLength={assignedActivities.length}
                  />
                );
              } else {
                return null;
              }
            })}
        </ScheduleContainer>
      </DndProvider>
      {showModal && (
        <EditActivityModal
          activity={
            selectedActivityIndex !== undefined
              ? assignedActivities[selectedActivityIndex]
              : undefined
          }
          hasClosed={() => setShowModal(false)}
          onSubmit={handleActivityEdit}
          isSavedActivity={false}
        />
      )}
    </OuterContainer>
  );
};
